import { useEffect, useRef, useState } from 'react'
import type { JSX } from 'react'

import { useSplit } from '@buffer-mono/features'
import {
  Button,
  XIcon,
  Flex,
  IconButton,
  Progress,
  Text,
  ZapFilledIcon,
  useSidebar,
} from '@buffer-mono/popcorn'

import {
  useCanStartTrial,
  useIsFreeUser,
  useIsOneBufferOrg,
  useOrganizationId,
  useOrganizationLimits,
} from '~publish/contexts/AccountContext'
import { useCta } from '~publish/hooks/useCta'
import { MODALS, useOpenGlobalModal } from '~publish/hooks/useOpenGlobalModal'
import { useTracking } from '~publish/legacy/tracking/useTracking'

import type { ChannelNavChannel } from '../useChannels'

import { getSlotIds } from '~publish/helpers/getSlotIds'

import styles from './ChannelLimitNotice.module.css'

const DISMISSED_KEY = 'channelLimitNotice-dismissed'
const CHANNEL_LIMIT_CTA = 'publish-sidebar-channels-upgradeForMore-1'
const CHANNEL_LIMIT_DISMISS_CTA = 'publish-sidebar-channels-dismiss-1'

interface ChannelLimitNoticeProps {
  channels: ChannelNavChannel[]
}

export function ChannelLimitNotice({
  channels,
}: ChannelLimitNoticeProps): JSX.Element | null {
  const limits = useOrganizationLimits()
  const organizationId = useOrganizationId()
  const isOneBufferOrg = useIsOneBufferOrg()
  const isFreeUser = useIsFreeUser()
  const canStartTrial = useCanStartTrial()
  const [isDismissed, setIsDismissed] = useState<boolean>(() => {
    return localStorage.getItem(DISMISSED_KEY) === 'true'
  })

  const { split: featureTrialSplit, isReady } = useSplit(
    'geid-feature-specific-trial',
  )
  const isInFeatureTrialSplit = isReady && featureTrialSplit?.treatment === 'on'

  const unlockedChannels = channels.filter((ch) => !ch.isLocked)
  const channelLimit = limits?.channels ?? 0

  const { clicked: ctaClicked } = useCta(CHANNEL_LIMIT_CTA, {
    upgradePathName: 'channelsLimitSidebar-upgrade',
    extraProperties: {
      currentChannelQuantity: unlockedChannels.length,
    },
  })

  const openGlobalModal = useOpenGlobalModal()

  const { open: sidebarOpen } = useSidebar()
  const { trackUpgradePathViewed, trackCTAClicked } = useTracking()
  const hasTrackedViewRef = useRef(false)

  const hasThreeChannels = unlockedChannels.length === 3

  const isVisible =
    isOneBufferOrg &&
    isFreeUser &&
    unlockedChannels.length <= 3 &&
    !isDismissed &&
    sidebarOpen

  // Track upgrade path viewed when the upgrade button is shown
  // Only track if the notice is actually visible with 3 channels
  useEffect(() => {
    if (
      isVisible &&
      hasThreeChannels &&
      organizationId &&
      !hasTrackedViewRef.current
    ) {
      trackUpgradePathViewed({
        upgradePathName: 'channelsLimitSidebar-upgrade',
        cta: CHANNEL_LIMIT_CTA,
      })
      hasTrackedViewRef.current = true
    }
  }, [isVisible, hasThreeChannels, organizationId, trackUpgradePathViewed])

  if (!isVisible) {
    return null
  }

  const handleDismiss = (): void => {
    trackCTAClicked({
      cta: CHANNEL_LIMIT_DISMISS_CTA,
      upgradePathName: 'channelsLimitSidebar-upgrade',
    })
    localStorage.setItem(DISMISSED_KEY, 'true')
    setIsDismissed(true)
  }

  const handleUpgrade = (): void => {
    ctaClicked()

    if (canStartTrial && isInFeatureTrialSplit) {
      openGlobalModal(MODALS.featureTrial, {
        feature: 'channelLimit',
        cta: CHANNEL_LIMIT_CTA,
        upgradePathName: 'channelsLimitSidebar-trial',
      })
    } else {
      openGlobalModal(MODALS.planSelector, {
        cta: CHANNEL_LIMIT_CTA,
        upgradePathName: 'channelsLimitSidebar-upgrade',
      })
    }
  }

  return (
    <Flex
      direction="column"
      gap="sm"
      className={styles.container}
      data-testid="channel-limit-notice"
    >
      <Flex
        align="center"
        justify="between"
        gap="xs"
        fullWidth
        className={styles.titleRow}
      >
        <Text
          as="p"
          size="sm"
          weight="semibold"
          color="default"
          truncate
          className={styles.title}
        >
          {unlockedChannels.length}/{channelLimit} channels connected
        </Text>
        <IconButton
          label="Dismiss"
          variant="tertiary"
          size="small"
          onClick={handleDismiss}
          className={styles.closeButton}
          data-testid="dismiss-button"
        >
          <XIcon />
        </IconButton>
      </Flex>
      <Flex gap="2xs" aria-hidden="true" data-testid="pill-container" fullWidth>
        {getSlotIds('channel-limit-progress', 3).map((slotId, index) => {
          const isConnected = index < unlockedChannels.length
          return (
            <Progress
              key={slotId}
              size="small"
              value={isConnected ? 100 : 0}
              className={styles.pill}
              data-testid="channel-pill"
              data-connected={isConnected}
            />
          )
        })}
      </Flex>
      {unlockedChannels.length === 3 && (
        <Button
          variant="secondary"
          size="small"
          onClick={handleUpgrade}
          className={styles.upgradeButton}
          data-testid="upgrade-button"
        >
          <ZapFilledIcon />
          {canStartTrial && isInFeatureTrialSplit
            ? 'Start Trial for More'
            : 'Upgrade for More'}
        </Button>
      )}
    </Flex>
  )
}
